import {
  appAuthAtom,
  AuthState,
  axiosErrorsAtom,
  IdTokenPayload,
  isAxiosError,
  LogoutReason,
} from '@eih-netbroker/common';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isAfter, subSeconds } from 'date-fns';
import { useSetAtom } from 'jotai';
import { User } from 'oidc-react';
import { useCallback, useEffect } from 'react';
import { axiosInstance } from '../../../context/AxiosInstanceContext';
import { store } from '../../state/store';
import { getJWTPayload } from '../../utils/jwtUtils';
import { useLogger } from '../../utils/useLogger';

/**
 * Trigger a silent token refresh when we are this close to (or past) the JWT expiry.
 * Provides a margin for clock skew and network latency so the request goes out with a
 * fresh token instead of one that expires mid-flight.
 */
const REFRESH_BUFFER_SECONDS = 30;

type UseAxiosInterceptorsEffectUserManager = {
  events: {
    addAccessTokenExpiring: (cb: () => Promise<void>) => void;
    removeAccessTokenExpiring: (cb: () => Promise<void>) => void;
  };
};

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

type UseAxiosInterceptorsEffectParams = {
  logout: (reason?: LogoutReason) => Promise<void>;
  refreshToken: () => Promise<User | null>;
  userManager: UseAxiosInterceptorsEffectUserManager;
};

type UseAxiosInterceptorsEffectOutput = void;

export const useAxiosInterceptorsEffect = ({
  logout,
  refreshToken,
  userManager,
}: UseAxiosInterceptorsEffectParams): UseAxiosInterceptorsEffectOutput => {
  const logger = useLogger('useAxiosInterceptorsEffect');
  const setAxiosErrors = useSetAtom(axiosErrorsAtom);

  const handleAxiosError = useCallback(
    async (error: any): Promise<unknown> => {
      if (isAxiosError(error)) {
        setAxiosErrors(prev => {
          let response = '-';
          let payload = '';
          let correlId = '';
          try {
            response = JSON.stringify(error.response);
          } catch (err) {}
          try {
            payload = JSON.stringify(error.config?.data);
          } catch (err) {}
          try {
            correlId = error.config?.headers.correlid || '';
          } catch (err) {}
          return [
            {
              when: new Date(),
              url: error.config?.url || '-',
              method: error.config?.method || '-',
              status: error.response?.status || 0,
              statusText: error.response?.statusText || '-',
              details: {
                correlId,
                payload,
                response,
              },
            },
            ...prev,
          ];
        });
      }

      const appAuthNow = store.get(appAuthAtom);
      if (appAuthNow.isAuthenticated && appAuthNow.authStatus !== AuthState.SignOff) {
        if (error.response?.status === 401) {
          const cfg = error.config as RetriableRequestConfig | undefined;

          if (cfg && !cfg._retriedAfterRefresh) {
            logger.warn(
              `Got 401 for ${(error as AxiosError).response?.config.url} — attempting token refresh and retry`,
            );
            try {
              const user = await refreshToken();
              if (user) {
                cfg._retriedAfterRefresh = true;
                cfg.headers.set('Authorization', `Bearer ${user.access_token}`);
                return axiosInstance.request(cfg);
              }
              // transient refresh failure — do not log out, just surface the original 401
            } catch (refreshErr) {
              logger.error(
                `Logout because refresh token is no longer usable (after 401 for ${(error as AxiosError).response
                  ?.config.url})`,
                refreshErr,
              );
              await logout(LogoutReason.TokenExpired);
            }
          } else {
            logger.error(
              `Logout because token is invalid (HTTP response is 401 for ${(error as AxiosError).response?.config.url}`,
            );
            await logout(LogoutReason.TokenExpired);
          }
        }
      }

      return Promise.reject(error);
    },
    [setAxiosErrors, logger, refreshToken, logout],
  );

  useEffect(() => {
    const id = axiosInstance.interceptors.response.use(res => res, handleAxiosError);
    return () => {
      axiosInstance.interceptors.response.eject(id);
    };
  }, [handleAxiosError]);

  useEffect(() => {
    axiosInstance.interceptors.request.use(async config => {
      const appAuth = store.get(appAuthAtom);

      // we have to set the Authorization header since openapi clients will strip any headers that are not explicitly defined in the spec
      if (!config.headers.get('Authorization') && appAuth.isAuthenticated) {
        const exp = getJWTPayload<IdTokenPayload>(appAuth.secrets.JwtToken).exp;
        const refreshAt = subSeconds(new Date(exp * 1000), REFRESH_BUFFER_SECONDS);

        if (isAfter(new Date(), refreshAt)) {
          // proactively (or reactively if already past expiry) refresh the token before the call
          try {
            const user = await refreshToken();
            // user === null means transient/network failure — fall back to the existing token;
            // if it is truly expired the server 401 will trigger the response interceptor retry
            const tokenToUse = user?.access_token ?? appAuth.secrets.JwtToken;
            config.headers.set('Authorization', `Bearer ${tokenToUse}`);
          } catch (err) {
            // auth-level refresh failure (invalid_grant, login_required, ...) — log the user out
            await logout(LogoutReason.TokenExpired);
            throw err;
          }
        } else {
          config.headers.set('Authorization', `Bearer ${appAuth.secrets.JwtToken}`);
        }
      }
      return Promise.resolve(config);
    });

    return () => {
      axiosInstance.interceptors.request.clear();
    };
  }, [refreshToken, userManager, logout]);

  useEffect(() => {
    const handleAccesTokenExpiring = async (): Promise<void> => {
      await refreshToken();
    };

    userManager.events.addAccessTokenExpiring(handleAccesTokenExpiring);

    return () => userManager.events.removeAccessTokenExpiring(handleAccesTokenExpiring);
  }, [refreshToken, userManager]);
};
